import type { RefObject } from 'react';

export function isAvailableFocusTarget(element: HTMLElement) {
  if (!element.isConnected || element.matches(':disabled')) return false;
  if (element.closest('[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]'))
    return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse'
    ) {
      return false;
    }
  }
  return true;
}

export function getTopVisibleModal() {
  const modals = document.querySelectorAll<HTMLElement>(
    '[aria-modal="true"][role="dialog"], [aria-modal="true"][role="alertdialog"]',
  );
  let top: HTMLElement | undefined;
  let topLayer = -Infinity;
  for (const modal of modals) {
    if (!isAvailableFocusTarget(modal)) continue;
    let layer = 0;
    for (let current: HTMLElement | null = modal; current; current = current.parentElement) {
      const zIndex = Number.parseInt(window.getComputedStyle(current).zIndex, 10);
      if (Number.isFinite(zIndex)) layer = Math.max(layer, zIndex);
    }
    if (layer >= topLayer) {
      top = modal;
      topLayer = layer;
    }
  }
  return top;
}

export function restoreOverlayTriggerFocus(
  triggerRef: RefObject<HTMLElement | null>,
  open: boolean,
  closingOverlay?: HTMLElement | null,
) {
  if (open) return;
  let stopped = false;
  let scheduled = false;
  let firstFrame: number | undefined;
  let secondFrame: number | undefined;
  let observer: MutationObserver | undefined;
  const stop = () => {
    stopped = true;
    observer?.disconnect();
    if (firstFrame !== undefined) window.cancelAnimationFrame(firstFrame);
    if (secondFrame !== undefined) window.cancelAnimationFrame(secondFrame);
    document.removeEventListener('focusin', onFocus, true);
    document.removeEventListener('keydown', stop, true);
    document.removeEventListener('pointerdown', stop, true);
  };
  const onFocus = (event: FocusEvent) => {
    const target = event.target;
    if (
      target === document.body ||
      target === document.documentElement ||
      target === triggerRef.current ||
      (target instanceof Node && closingOverlay?.contains(target))
    )
      return;
    stop();
  };
  const restore = () => {
    if (stopped) return;
    const trigger = triggerRef.current;
    if (!trigger || !trigger.isConnected || trigger.matches(':disabled')) {
      stop();
      return;
    }
    if (closingOverlay) {
      const active = document.activeElement;
      if (active === trigger) {
        stop();
        return;
      }
      if (
        active &&
        active !== document.body &&
        active !== document.documentElement &&
        !closingOverlay.contains(active)
      ) {
        stop();
        return;
      }
    }
    const modal = getTopVisibleModal();
    if (modal && !modal.contains(trigger)) {
      // Only the caller's closing overlay may defer this return, never a new modal.
      if (!closingOverlay?.contains(modal)) stop();
      return;
    }
    if (!isAvailableFocusTarget(trigger)) {
      // Overlay ownership can release inert/aria-hidden after its motion ends.
      if (!closingOverlay || !trigger.closest('[inert], [aria-hidden="true"]')) stop();
      return;
    }
    stop();
    trigger.focus({ preventScroll: true });
  };
  const schedule = () => {
    if (stopped || scheduled) return;
    scheduled = true;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        scheduled = false;
        restore();
      });
    });
  };
  if (closingOverlay) {
    // One consumer-owned close session: event driven, with no timer or rAF retry loop.
    observer = new MutationObserver(schedule);
    observer.observe(closingOverlay, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    if (closingOverlay.parentElement)
      observer.observe(closingOverlay.parentElement, {
        attributes: true,
        childList: true,
      });
    for (let element = triggerRef.current; element; element = element.parentElement) {
      observer.observe(element, {
        attributes: true,
        childList: true,
        attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden', 'aria-disabled'],
      });
    }
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('keydown', stop, true);
    document.addEventListener('pointerdown', stop, true);
  }
  schedule();
  return stop;
}
